import React from "react";
import { CardComp } from "../../../../components";
import { formatMoney } from "../../../../utils";
import moment from "moment";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { convertToTitleCase } from "../../../../utils/helpers";

const ContributionDetailsModal = ({ contribution, handleClose }) => {
  if (!contribution) {
    return (
      <div className="py-4">
        <div className="text-center py-5">
          <p className="text-muted">No contribution data available</p>
        </div>
      </div>
    );
  }

  // Handle member name - support both string and object formats
  let memberName = "N/A";
  if (typeof contribution?.member === 'string') {
    memberName = contribution.member;
  } else if (contribution?.member?.first_name && contribution?.member?.last_name) {
    memberName = `${contribution.member.first_name} ${contribution.member.last_name}`;
  } else if (contribution?.member?.name) {
    memberName = contribution.member.name;
  }

  const memberPhoto = contribution?.memberImage || contribution?.member?.photo;
  const memberNo = contribution?.memberId || contribution?.member?.member_no;
  const memberEmail = contribution?.member?.email;
  const memberPhone = contribution?.member?.phone;

  // Handle contribution category
  const contributionCategory = contribution?.contributionCategory || contribution?.category?.name || "N/A";
  
  // Handle amount - support multiple formats
  let totalAmount = 0;
  if (contribution?.totalAmount) {
    // If it's a formatted string like "11,000.00", parse it
    const parsed = typeof contribution.totalAmount === 'string' 
      ? parseFloat(contribution.totalAmount.replace(/,/g, '')) 
      : contribution.totalAmount;
    totalAmount = isNaN(parsed) ? 0 : parsed;
  } else {
    totalAmount = contribution?.amount ?? contribution?.total_amount ?? 0;
  }

  // Handle amount remaining
  let amountRemaining = "N/A";
  if (contribution?.amountRemaining) {
    const parsed = typeof contribution.amountRemaining === 'string'
      ? parseFloat(contribution.amountRemaining.replace(/,/g, ''))
      : contribution.amountRemaining;
    amountRemaining = isNaN(parsed) ? "N/A" : parsed;
  } else if (contribution?.amount_remaining !== undefined && contribution?.amount_remaining !== null) {
    amountRemaining = contribution.amount_remaining;
  }

  const currency = contribution?.currency || "NGN";
  const branch = typeof contribution?.branch === 'string' ? contribution.branch : (contribution?.branch?.name || "N/A");
  
  // Handle status - contribution status or member status
  const status = contribution?.status || (contribution?.member?.status ? convertToTitleCase(contribution.member.status) : "N/A");
  
  const createdAt = contribution?.createdAt || contribution?.created_at
    ? moment(contribution.createdAt || contribution.created_at).format("DD-MMM-YYYY HH:mm")
    : "N/A";

  const getRecorder = () =>
    contribution?.recordedBy ||
    contribution?.recorded_by ||
    contribution?.initiator ||
    null;

  const recordedBy = getRecorder();

  const getRecorderName = () => {
    if (!recordedBy) return "Member";
    if (typeof recordedBy === "string") return recordedBy;
    const name = [recordedBy?.first_name, recordedBy?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (name) return name;
    if (recordedBy?.name) return recordedBy.name;
    if (recordedBy?.email) return recordedBy.email;
    return "Member";
  };

  const getRecorderType = () => {
    if (!recordedBy?.type) return "Staff";
    return convertToTitleCase(recordedBy.type.replace("Company", ""));
  };

  const recordedAtLabel =
    contribution?.createdAt || contribution?.created_at
      ? moment(contribution?.createdAt || contribution?.created_at).format(
          "DD-MMM-YYYY HH:mm"
        )
      : "N/A";

  // Status styles for contributions (Active, Suspended, etc.)
  const statusColors = {
    ACTIVE: "#19BC31",
    SUSPENDED: "#FF4343",
    PENDING: "#FFA500",
    COMPLETED: "var(--color-secondary)",
  };
  const statusColor = statusColors[status?.toUpperCase()] || "#A1A0A0";

  return (
    <div className="py-3">
      <div className="text-center mb-4">
        <h3 className="text-primary1 fw-bold mb-2">Contribution Details</h3>
        <div
          className="border-bottom border-2 border-primary1 mx-auto"
          style={{ width: "100px" }}
        ></div>
      </div>

      <div className="row g-3">
        {/* Member Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Member Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-2">
                  <FallbackImage
                    className="rounded-circle me-3"
                    width="50px"
                    height="50px"
                    src={memberPhoto}
                  />
                  <div>
                    <h6 className="mb-0 fw-bold">{memberName}</h6>
                    {memberNo && (
                      <small className="text-muted">
                        #{typeof memberNo === 'string' && memberNo.startsWith('#') ? memberNo.substring(1) : memberNo}
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                {memberEmail && (
                  <div className="mb-2">
                    <small className="text-muted d-block">Email</small>
                    <span>{memberEmail}</span>
                  </div>
                )}
                {memberPhone && (
                  <div className="mb-2">
                    <small className="text-muted d-block">Phone</small>
                    <span>{memberPhone}</span>
                  </div>
                )}
              </div>
            </div>
          </CardComp>
        </div>

        {/* Contribution Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Contribution Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Contribution Category</small>
                  <span className="fw-bold">{contributionCategory}</span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Total Amount</small>
                  <span className="fw-bold text-primary1">
                    {formatMoney(totalAmount, true)}
                  </span>
                </div>
                {amountRemaining !== "N/A" && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Amount Remaining</small>
                    <span className="fw-bold">
                      {typeof amountRemaining === 'number' 
                        ? formatMoney(amountRemaining, true)
                        : amountRemaining}
                    </span>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Status</small>
                  <div
                    className="px-3 py-1 rounded-pill text-white d-inline-block"
                    style={{
                      backgroundColor: statusColor,
                    }}
                  >
                    {convertToTitleCase(status)}
                  </div>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Currency</small>
                  <span className="fw-bold">{currency}</span>
                </div>
                {branch !== "N/A" && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Branch</small>
                    <span className="fw-bold">{branch}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3 mt-2">
              {createdAt !== "N/A" && (
                <div className="col-md-6">
                  <small className="text-muted d-block mb-1">Created At</small>
                  <span className="fw-bold">{createdAt}</span>
                </div>
              )}
              {contribution?.id && (
                <div className="col-md-6">
                  <small className="text-muted d-block mb-1">Contribution ID</small>
                  <span className="fw-bold">{contribution.id}</span>
                </div>
              )}
            </div>
          </CardComp>
        </div>

        {/* Audit Trail */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Audit Trail</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Recorded by</small>
                  <span className="fw-bold">{getRecorderName()}</span>
                </div>
                {recordedBy?.type && (
                  <div>
                    <small className="text-muted d-block mb-1">User Type</small>
                    <span>{getRecorderType()}</span>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Recorded on</small>
                  <span>{recordedAtLabel}</span>
                </div>
                {recordedBy?.email && (
                  <div>
                    <small className="text-muted d-block mb-1">Contact</small>
                    <span>{recordedBy.email}</span>
                  </div>
                )}
              </div>
            </div>
          </CardComp>
        </div>
      </div>
    </div>
  );
};

export default ContributionDetailsModal;

